import { createContext, useContext, useEffect, useState } from "react";
import i18n, { normalizeLang } from "@/i18n";
import { api, saveAuth, clearAuth, getStoredUser, getToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(getStoredUser());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const t = getToken();
    if (!t) return;

    // The locally cached profile makes the first paint fast, but it must not
    // be treated as authoritative. Roles and other security-relevant fields
    // can change server-side (for example when the owner is promoted to
    // platform admin), so reconcile the cache on every fresh app load.
    api.get("/auth/me")
      .then((r) => {
        setUser(r.data);
        try { localStorage.setItem("crd_sport_user", JSON.stringify(r.data)); } catch { /* ignore */ }
      })
      .catch((error) => {
        // A temporary outage must not destroy a valid session.
        if (error?.response?.status === 401) {
          clearAuth();
          setUser(null);
        }
      });
  }, []); // eslint-disable-line

  const login = async (email, password) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      saveAuth(data.token, data.user);
      setUser(data.user);
      // Sync UI to the stored preference if user has one; never override their
      // current explicit selection if the backend value matches default.
      if (data.user?.preferred_language) {
        try { await i18n.changeLanguage(normalizeLang(data.user.preferred_language)); } catch { /* ignore */ }
      }
      return data.user;
    } finally {
      setLoading(false);
    }
  };

  const register = async (payload) => {
    setLoading(true);
    try {
      const body = { ...payload, preferred_language: normalizeLang(i18n.language) };
      const { data } = await api.post("/auth/register", body);
      saveAuth(data.token, data.user);
      setUser(data.user);
      return data.user;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    clearAuth();
    setUser(null);
    window.location.href = "/";
  };

  const refreshUser = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      // Keep the persisted `crd_sport_user` in sync (legacy key preserved).
      try { localStorage.setItem("crd_sport_user", JSON.stringify(data)); } catch { /* ignore */ }
      return data;
    } catch {
      return null;
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
